import { EditIcon, TrashIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { FormDetailPane } from '@/components/common/FormDetailPane';
import { TextFormField } from '@/components/common/FormField';
import { Button } from '@/components/ui/button';
import { handleApiError } from '../lib/errors';

export interface CostGroupForDrawer {
  id: string;
  name: string;
}

interface CostGroupDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  group?: CostGroupForDrawer | null;
  onSave: (name: string, id?: string) => undefined | Promise<unknown>;
  readOnly?: boolean;
  onEditRequest?: () => void;
  onDeleteRequest?: () => void;
}

export const CostGroupDrawer: React.FC<CostGroupDrawerProps> = ({
  isOpen,
  onClose,
  group = null,
  onSave,
  readOnly = false,
  onEditRequest,
  onDeleteRequest,
}) => {
  const isEdit = !!group?.id && !readOnly;
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setName(group?.name ?? '');
      setError(null);
      setSubmitError(null);
    }
  }, [isOpen, group?.id, group?.name]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Group name is required');
      return;
    }
    setError(null);
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      await Promise.resolve(onSave(trimmed, group?.id));
      onClose();
    } catch (err) {
      setSubmitError(handleApiError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormDetailPane
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={readOnly ? 'View cost group' : isEdit ? 'Edit cost group' : 'Add cost group'}
      description={
        readOnly
          ? 'Cost group details.'
          : isEdit
            ? 'Update the cost group name.'
            : 'Enter the name for the new cost group.'
      }
      onSubmit={readOnly ? undefined : handleSubmit}
      submitLabel={isEdit ? 'Update group' : 'Add group'}
      isSubmitting={isSubmitting}
      submitError={submitError}
      showFooter={!readOnly}
    >
      {readOnly ? (
        <div className="flex flex-wrap justify-end gap-2 border-b border-border-default pb-4">
          {onEditRequest ? (
            <Button type="button" size="sm" onClick={onEditRequest}>
              <EditIcon data-icon="inline-start" />
              Edit
            </Button>
          ) : null}
          {onDeleteRequest ? (
            <Button type="button" size="sm" variant="destructive" onClick={onDeleteRequest}>
              <TrashIcon data-icon="inline-start" />
              Delete
            </Button>
          ) : null}
        </div>
      ) : null}
      <TextFormField
        label="Group name"
        required
        value={name}
        onChange={(v) => {
          setName(v);
          if (error) setError(null);
        }}
        error={error ?? undefined}
        placeholder="Enter group name"
        disabled={readOnly}
        copyable={readOnly}
      />
    </FormDetailPane>
  );
};
